import './DailyJournalFullScreen.scss'

import React, { useEffect, useRef, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { useParams, useHistory } from 'react-router-dom'
import { navigateTo } from '../../../common/navigation'
import moment from 'moment'
import {
  getDailyJournalData,
  getUserJournalsByID,
  createJournalEntry,
  getUserJournalsEntriesByID,
  deleteJournalEntry
} from '../../../common/api/Practices'
import {
  ROUTE_ACTIVITIES_JOURNALING_SAVED_JOURNAL_ALL,
  ROUTE_ACTIVITIES_JOURNALING_ALL
} from '../../../common/helpers/Paths'

import {
  EVENT_CATEGORY_JOURNALING,
  EVENT_ACTION_PAGEVIEW,
  saveUserEvent,
  EVENT_ACTION_DELETE,
  EVENT_VALUE_ALL,
  EVENT_ACTION_SAVE
} from '../../../common/api/UserEvents'
import { app } from '../../../../config'
import EmojiPicker from '../JournalingContainer/EmojiPicker'
import JournalingContainer from '../JournalingContainer'
import JournalEntryHeading from '../JournalingContainer/JournalEntryHeading'
import JournalEntryContainer from '../JournalingContainer/JournalEntryContainer'
import JournalEntryButtons from '../JournalingContainer/JournalEntryButtons'
import { KEY_PRACTICE_JOURNALING_WP } from 'components/common/api'
import Bookmark from 'components/bookmark/Bookmark'

export default function DailyJournalFullScreen({
  setHeaderTitle,
  setReturnPath,
  setShowToast,
  setToastMessage
}) {
  const history = useHistory()

  const { journalingId, journalEntryId } = useParams()
  const [prompt, setPrompt] = useState()

  const [isLoading, setIsLoading] = useState(true)
  const [journalContent, setJournalContent] = useState('')
  const [chosenEmoji, setChosenEmoji] = useState('😀')
  const [isButtonLoading, setIsButtonLoading] = useState(false)
  const [journalData, setJournalData] = useState([])
  const [activeIndex, setActiveIndex] = useState(0)
  const [pastJournalID, setPastJournalID] = useState()
  const [isDeleteLoading, setIsDeleteLoading] = useState(false)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [checkContent, setCheckContent] = useState(true)

  let content = useRef(null)

  useEffect(() => {
    if (content.current) {
      content.current.value = journalContent
    }

    setCheckContent(!Boolean(journalContent))
  }, [journalContent])

  const getUserJournalsByIDAsync = async () => {
    //return early
    if (!prompt) return

    const result = await getUserJournalsByID(prompt.id)
    // if there is no length, then there is no journal entry
    if (!result.length) {
      setIsLoading(false)
      return
    }

    // set journalHistory for desktop list.
    setJournalData(result || [])

    // set the id of the current journal entry
    const entry = result.find(entry => entry.id === parseInt(journalEntryId))

    if (entry) setActiveIndex(entry.id)

    const journalsMadeToday = result.filter(journal =>
      moment().isSame(journal.created_on, 'day')
    )

    if (journalsMadeToday.length > 0 && !journalEntryId) {
      setJournalContent(journalsMadeToday[0].journal_text || '') //just to be safe
      setPastJournalID(journalsMadeToday[0].id)
    }
    saveUserEvent(
      EVENT_CATEGORY_JOURNALING,
      EVENT_ACTION_PAGEVIEW,
      EVENT_VALUE_ALL
    )
    setIsLoading(false)
  }

  const onEmojiClick = (event, emojiObject) => {
    setChosenEmoji(emojiObject.emoji)
    setShowEmojiPicker(prevCheck => !prevCheck)
  }

  const handleShowEmojiPicker = () => {
    setShowEmojiPicker(prevCheck => !prevCheck)
  }

  const handleSave = async () => {
    setIsButtonLoading(true)

    const body = {
      journal_id: prompt.practiceId,
      journal_text: content.current.value,
      journal_emoji: chosenEmoji,
      previousId: pastJournalID || ''
    }

    const response = await createJournalEntry(body)
    //if response is error object, show error message
    const message = response.error
      ? 'An error occurred. Please wait a moment and try again.'
      : 'Journal entry saved'

    saveUserEvent(EVENT_CATEGORY_JOURNALING, EVENT_ACTION_SAVE, EVENT_VALUE_ALL)
    setToastMessage(message)
    setShowToast(true)
    setIsButtonLoading(false)

    getUserJournalsByIDAsync()
  }

  const handleDelete = async () => {
    if (!pastJournalID) return
    setIsDeleteLoading(true)
    const response = await deleteJournalEntry(pastJournalID)
    if (response.error) {
      setToastMessage('An error occurred. Please try again.')
    } else {
      saveUserEvent(
        EVENT_CATEGORY_JOURNALING,
        EVENT_ACTION_DELETE,
        EVENT_VALUE_ALL
      )
      setToastMessage('Journal entry deleted!')
      const updated = journalData.filter(
        item => item.pastJournalID !== pastJournalID
      )
      setJournalData(updated)
      setJournalContent('')
    }
    setShowToast(true)
    setIsDeleteLoading(false)

    navigateTo(ROUTE_ACTIVITIES_JOURNALING_SAVED_JOURNAL_ALL, history)
  }

  // Validates if its free entry category and sets the title, if not its a daily journal and sets the promps
  useEffect(() => {
    setIsLoading(true)
    setHeaderTitle('Journal')

    // if its free entry, set the return path to saved journals
    const returnPath =
      journalEntryId &&
      journalingId === app.practices.journaling.free_entry_cat_id //and if its desktop
        ? ROUTE_ACTIVITIES_JOURNALING_SAVED_JOURNAL_ALL
        : ROUTE_ACTIVITIES_JOURNALING_ALL

    setReturnPath(returnPath)

    setPastJournalID(journalEntryId)

    if (journalingId === app.practices.journaling.free_entry_cat_id) {
      //set prompt to free journaling
      setPrompt({
        id: journalingId,
        title: 'Free Journaling',
        practiceId: journalingId
      })
    } else {
      ;(async function getJournalDataAsync() {
        const result = (await getDailyJournalData(journalingId)) || []
        setPrompt(result)
      })()
    }

    // Log access
    saveUserEvent(
      EVENT_CATEGORY_JOURNALING,
      EVENT_ACTION_PAGEVIEW,
      journalingId
    )

    setIsLoading(false)
  }, [journalingId, setHeaderTitle, setReturnPath, journalEntryId])

  // Gets the journal entries for the journaling, if its a daily journal it gets the past entries
  useEffect(() => {
    setIsLoading(true)
    getUserJournalsByIDAsync()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prompt, journalEntryId])

  useEffect(() => {
    if (!journalEntryId) return
    setIsLoading(true)

    async function getJournalDataByIdAsync() {
      const response = await getUserJournalsEntriesByID(journalEntryId)
      if (!response) {
        setIsLoading(false)
        return
      }
      setChosenEmoji(response[0].journal_emoji || '😀')
      setJournalContent(response[0].journal_text)

      setIsLoading(false)
    }

    getJournalDataByIdAsync()
  }, [journalEntryId])

  const handleTextAreaChange = () =>
    setCheckContent(content.current?.value === '')

  const pastEntriesClick = () => {
    navigateTo(
      ROUTE_ACTIVITIES_JOURNALING_SAVED_JOURNAL_ALL + '/' + prompt.practiceId,
      history
    )
  }

  return (
    <JournalingContainer
      isLoading={isLoading}
      journalData={journalData}
      journalListHeading={'Journal History'}
      isButtonLoading={isButtonLoading}
      activeIndex={activeIndex}
    >
      <Bookmark
        category={KEY_PRACTICE_JOURNALING_WP}
        categoryObjectId={journalingId}
        color='text-black'
      />
      {journalData.length > 0 && !journalEntryId && (
        <Row
          className='journal-past-entries-content'
          onClick={pastEntriesClick}
          xs={2}
        >
          <Col className='journal-past-entries-title'>
            <h4>Past entries</h4>
          </Col>
          <Col className='journal-past-entries-counter'>
            ({journalData.length})
          </Col>
        </Row>
      )}
      <Container className='daily-journal'>
        <Row>
          <Col>
            <JournalEntryHeading
              classNames={'p-3 p-md-4'}
              chosenEmoji={chosenEmoji}
              prompt={prompt && prompt.title}
              handleShowEmojiPicker={handleShowEmojiPicker}
            />
          </Col>
        </Row>
        <Row className='emoji-container position-relative mt-0 mt-md-3 mb-4'>
          <div className='position-absolute end-0'>
            <EmojiPicker
              showEmojiPicker={showEmojiPicker}
              onEmojiClick={onEmojiClick}
            />
          </div>
          <Col>
            <JournalEntryContainer
              value={journalContent}
              journalContent={content}
              handleTextAreaChange={handleTextAreaChange}
            />
          </Col>
        </Row>
        <Row className='journal-buttons d-flex justify-content-between'>
          <Col>
            <JournalEntryButtons
              handleClick={handleSave}
              checkContent={checkContent}
              isButtonLoading={isButtonLoading}
              isDeleteLoading={isDeleteLoading}
              pastJournalID={pastJournalID}
              handleDelete={handleDelete}
              showDeleteButton={true}
            >
              {isButtonLoading ? 'Saving...' : 'Save'}
            </JournalEntryButtons>
          </Col>
        </Row>
      </Container>
    </JournalingContainer>
  )
}
